import React, { useCallback, useMemo } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { Select, Stack, TextInput } from '@mantine/core'

import { getErrataRecipientOptions } from './errataRecipientOptions'

import { FormErrors } from '@/components/Event/NewScript/components/common/forms/FormErrors'
import { ActionContainer } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionContainer'
import { ActionText } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionText'
import { ActionTitle } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionTitle'
import { ScriptAttendee } from '@/types'

type ErrataRecipientFormValues = {
  errata_recipient_attendee_id?: string | null
  attendees: ScriptAttendee[]
}

export const ErrataRecipientContent: React.FC = () => {
  const { control, formState, setValue, trigger } =
    useFormContext<ErrataRecipientFormValues>()

  const watchedAttendees = useWatch({ control, name: 'attendees' })
  const attendees = useMemo(
    () => (watchedAttendees ?? []) as ScriptAttendee[],
    [watchedAttendees],
  )
  const options = useMemo(
    () => getErrataRecipientOptions(attendees),
    [attendees],
  )

  const selectedId = useWatch({
    control,
    name: 'errata_recipient_attendee_id',
  })
  const selectedIndex = attendees.findIndex(
    attendee => attendee.attendee_id === selectedId,
  )
  const emailPath = `attendees.${selectedIndex}.email` as const

  const onEmailChange = useCallback(
    (value: string) => {
      setValue(emailPath, value, { shouldDirty: true, shouldValidate: true })
      trigger('errata_recipient_attendee_id')
    },
    [emailPath, setValue, trigger],
  )

  const recipientError = formState.errors.errata_recipient_attendee_id?.message
  const emailError =
    selectedIndex === -1
      ? undefined
      : formState.errors.attendees?.[selectedIndex]?.email?.message

  return (
    <ActionContainer outer>
      <Stack>
        <ActionContainer>
          <Stack>
            <Controller
              control={control}
              name="errata_recipient_attendee_id"
              render={({ field: { onChange, value } }) => (
                <Select
                  value={value ?? null}
                  onChange={onChange}
                  data={options}
                  placeholder="Select attendee"
                  label="Errata recipient"
                  clearable
                  error={!!recipientError}
                  style={{ width: 360 }}
                />
              )}
            />
            {recipientError !== undefined && (
              <FormErrors errors={recipientError} />
            )}
          </Stack>
        </ActionContainer>
        {selectedIndex !== -1 && (
          <ActionContainer>
            <Stack>
              <TextInput
                value={attendees[selectedIndex]?.email ?? ''}
                onChange={event => onEmailChange(event.currentTarget.value)}
                label="Recipient email"
                placeholder="name@example.com"
                error={!!emailError}
                style={{ width: 360 }}
              />
              {emailError !== undefined && <FormErrors errors={emailError} />}
            </Stack>
          </ActionContainer>
        )}
        <ActionContainer>
          <ActionTitle>Reminder</ActionTitle>
          <ActionText>
            The errata sheet usually goes to the attorney answering on the
            witness’ behalf, or to the witness if they are not represented. Ask
            for an email address if we do not have one yet.
          </ActionText>
        </ActionContainer>
      </Stack>
    </ActionContainer>
  )
}
